import { useReducer } from "react";

import {
  DropdownValue,
  NutrientDetailsDropdownOptions,
  TenantNutrientType
} from "api/models";
import { hashCodeOfString } from "api/tools";

// Reducer Types
export enum NutrientDetailsFormActionType {
  ON_PAGE_LOAD = "on-page-load",
  CHANGE_FORM_STATE = "change-form-state"
}

export enum NutrientDetailsFormActionPayloadType {
  FORM_DETAILS = "formDetails",
  VALIDATION_DETAILS = "validationDetails"
}

export const RATIO_VALUE = "ratio";

export type FormDetails = {
  nutrientName?: string;
  nutrientCode?: string;
  nutrientUnit?: string;
  nutrientUnitKey?: string;
  numeratorNutrientKey?: string;
  denominatorNutrientKey?: string;
  nutrientPackages?: string[];
  nutrientOvsName?: string;
  isRatio?: boolean;
  selectedFamily?: string;
  selectedClass?: string;
  nutrientType?: string;
  nutrientAutoCalcName?: string;
};

export type ValidationDetails = {
  nutrientNameValidationMessage?: string;
  nutrientCodeValidationMessage?: string;
  nutrientUnitValidationMessage?: string;
  nutrientNumeratorValidationMessage?: string;
  nutrientDenominatorValidationMessage?: string;
  nutrientOvsNameValidationMessage?: string;
  nutrientSelectedClassValidationMessage?: string;
  nutrientAutoCalcNameValidationMessage?: string;
};

export type NutrientDetailsFormState = {
  isFormDirty?: boolean;
  formDetails?: FormDetails;
  validationDetails?: ValidationDetails;
  key?: string;
  nutrientUnits?: DropdownValue[] | undefined;
  ratioUnitKey?: string;
  nutrientPackageOptions?: DropdownValue[] | undefined;
  stateHash?: string;
  nutrientOptions?: DropdownValue[] | undefined;
  nutrientDetailsDropdownOptions?: NutrientDetailsDropdownOptions;
  nutrientTypeOptions?: TenantNutrientType[] | undefined;
};

export type NutrientDetailsFormAction =
  | {
      type: NutrientDetailsFormActionType.ON_PAGE_LOAD;
      payload: NutrientDetailsFormState;
    }
  | {
      type: NutrientDetailsFormActionType.CHANGE_FORM_STATE;
      payload: {
        key: NutrientDetailsFormActionPayloadType;
        value: FormDetails | ValidationDetails;
      };
    };

export const initState: NutrientDetailsFormState = {
  isFormDirty: false,
  formDetails: {
    nutrientName: "",
    nutrientCode: "",
    nutrientUnit: "",
    nutrientUnitKey: "",
    numeratorNutrientKey: "",
    denominatorNutrientKey: "",
    nutrientPackages: [],
    nutrientOvsName: "",
    isRatio: false,
    nutrientType: undefined,
    nutrientAutoCalcName: ""
  },
  validationDetails: {
    nutrientNameValidationMessage: "",
    nutrientCodeValidationMessage: "",
    nutrientUnitValidationMessage: "",
    nutrientNumeratorValidationMessage: "",
    nutrientDenominatorValidationMessage: "",
    nutrientOvsNameValidationMessage: "",
    nutrientSelectedClassValidationMessage: "",
    nutrientAutoCalcNameValidationMessage: ""
  },
  key: "",
  ratioUnitKey: "",
  nutrientUnits: [],
  nutrientPackageOptions: [],
  nutrientOptions: [],
  nutrientDetailsDropdownOptions: {
    families: []
  },
  nutrientTypeOptions: [],
  stateHash: ""
};

function getValidationDetailsForFormChange(
  state: NutrientDetailsFormState,
  formDetails?: FormDetails
): ValidationDetails {
  return {
    ...state.validationDetails,
    nutrientUnitValidationMessage:
      formDetails?.nutrientUnitKey !== state.formDetails?.nutrientUnitKey
        ? ""
        : state.validationDetails?.nutrientUnitValidationMessage,
    nutrientNumeratorValidationMessage:
      formDetails?.numeratorNutrientKey !==
        state.formDetails?.numeratorNutrientKey ||
      formDetails?.isRatio !== state.formDetails?.isRatio
        ? ""
        : state.validationDetails?.nutrientNumeratorValidationMessage,
    nutrientDenominatorValidationMessage:
      formDetails?.denominatorNutrientKey !==
        state.formDetails?.denominatorNutrientKey ||
      formDetails?.isRatio !== state.formDetails?.isRatio
        ? ""
        : state.validationDetails?.nutrientDenominatorValidationMessage,
    nutrientSelectedClassValidationMessage:
      formDetails?.selectedFamily !== state.formDetails?.selectedFamily ||
      formDetails?.selectedClass !== state.formDetails?.selectedClass
        ? ""
        : state.validationDetails?.nutrientSelectedClassValidationMessage,
    nutrientAutoCalcNameValidationMessage:
      formDetails?.nutrientAutoCalcName !==
      state.formDetails?.nutrientAutoCalcName
        ? ""
        : state.validationDetails?.nutrientAutoCalcNameValidationMessage,
    nutrientOvsNameValidationMessage:
      formDetails?.nutrientOvsName !== state.formDetails?.nutrientOvsName
        ? ""
        : state.validationDetails?.nutrientOvsNameValidationMessage
  };
}

// Reducer Function Logic
function reducer(
  state: NutrientDetailsFormState,
  action: NutrientDetailsFormAction
) {
  switch (action.type) {
    case NutrientDetailsFormActionType.ON_PAGE_LOAD: {
      const initialLoadState = { ...state, ...action.payload };
      return {
        ...initialLoadState,
        isFormDirty: false,
        stateHash: hashCodeOfString(
          JSON.stringify(initialLoadState.formDetails)
        )
      };
    }
    case NutrientDetailsFormActionType.CHANGE_FORM_STATE: {
      const updatedDetails = {
        ...state,
        [action.payload.key]: action.payload.value
      };

      if (
        action.payload.key === NutrientDetailsFormActionPayloadType.FORM_DETAILS
      ) {
        updatedDetails.validationDetails = getValidationDetailsForFormChange(
          state,
          updatedDetails.formDetails
        );
      }

      if (updatedDetails.formDetails) {
        if (updatedDetails.formDetails?.selectedFamily) {
          const classes =
            state?.nutrientDetailsDropdownOptions?.families?.find(
              x => x.keyValue === updatedDetails?.formDetails?.selectedFamily
            )?.classes ?? [];
          if (classes.length === 1) {
            updatedDetails.formDetails.selectedClass = classes[0].keyValue;
          }
        } else {
          updatedDetails.formDetails.selectedClass = undefined;
        }

        updatedDetails.formDetails.isRatio =
          !!updatedDetails.ratioUnitKey &&
          updatedDetails.formDetails.nutrientUnitKey ===
            updatedDetails.ratioUnitKey;
      }

      const hashCodeAfterChange = hashCodeOfString(
        JSON.stringify(updatedDetails.formDetails)
      );

      return {
        ...updatedDetails,
        isFormDirty: hashCodeAfterChange !== state.stateHash
      };
    }
    default:
      return state;
  }
}

// Component
export default function useNutrientDetailsFormReducer(
  init?: NutrientDetailsFormState
): [NutrientDetailsFormState, React.Dispatch<NutrientDetailsFormAction>] {
  const [newState, dispatch] = useReducer(reducer, init ?? initState);
  return [newState, dispatch];
}
